import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AuthContext from "../../contexts/AuthContext/AuthContext";
import { getMySubscription } from "../../services/GameService";
import "./LevelAdvanceNotice.css";

// Shown on a game's game-over screen to tell the player how to unlock higher
// levels (#108 + #115):
//   * Not signed in  -> "Log in to advance to higher levels and save progress".
//   * Free tier       -> "Subscribe to unlock all levels" (trial ended / none).
//   * Trial / subscribed -> nothing (they already progress past Level 1).
//
// Anonymous users always stop at Level 1 (the backend only serves Level 1 to
// them), and free-tier users are capped at Level 1 of non-free games, so this
// is the natural place to surface the prompt.
const LevelAdvanceNotice = () => {
  // Tolerate being rendered without a provider (e.g. isolated game-over tests).
  const auth = useContext(AuthContext) || {};
  const isSignedIn = Boolean(auth.isSignedIn);
  const [hasFullAccess, setHasFullAccess] = useState(null);

  useEffect(() => {
    let active = true;
    if (!isSignedIn) {
      setHasFullAccess(null);
      return undefined;
    }
    (async () => {
      try {
        const sub = await getMySubscription();
        if (active) setHasFullAccess(Boolean(sub && sub.has_full_access));
      } catch (e) {
        // On error, assume access so we never nag a paying user by mistake.
        if (active) setHasFullAccess(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [isSignedIn]);

  // Not signed in: prompt to log in to advance + save progress (#108).
  if (!isSignedIn) {
    return (
      <div className="level-advance-notice" role="status">
        <p className="level-advance-notice__text">
          🔒 Log in to advance to higher levels and save your progress.
        </p>
        <Link className="level-advance-notice__btn" to="/signin">
          Log in
        </Link>
      </div>
    );
  }

  // Signed in but on the free tier (trial ended / never subscribed): prompt to
  // subscribe to unlock levels beyond the free Level 1 (#115).
  if (hasFullAccess === false) {
    return (
      <div className="level-advance-notice" role="status">
        <p className="level-advance-notice__text">
          ✨ You've reached the end of the free level. Subscribe to unlock all
          levels.
        </p>
        <Link className="level-advance-notice__btn" to="/pricing">
          See plans
        </Link>
      </div>
    );
  }

  // Trial or subscribed: nothing to prompt.
  return null;
};

export default LevelAdvanceNotice;
